import cn from 'classnames';
import { every, flattenDeep, indexOf } from 'lodash';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import IconCheck from '@/assets/icon-check.svg?react';
import CompressorsGroup from '@/comp/pages/EnginePage/Engine/Compressors/CompressorsGroup';
import DefaultSetpoints from '@/comp/pages/EnginePage/Engine/DefaultRule/DefaultSetpoints';
import FlowLevels from '@/comp/pages/EnginePage/Engine/RulesGenerator/FlowLevels';
import PressureLevelsComponent from '@/comp/pages/EnginePage/Engine/RulesGenerator/PressureLevels';
import MainRangesResult from '@/comp/pages/EnginePage/Engine/RulesGenerator/Result';
import SensorsPriorityResult from '@/comp/pages/EnginePage/Engine/Sensors/Result';
import SensorsPriority from '@/comp/pages/EnginePage/Engine/Sensors/SensorsPriority';
import SpinCentered from '@/comp/SpinCentered';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';

import {
  BASE_PAGE_INDEX,
  CompressorPriority,
  DefaultBehaviour,
  EngineConfigStatus,
  EngineStepsEnum,
  EngineSubstepsEnum,
  FallbackBehaviour,
  SetpointsConfiguration,
  subStepsWithoutBackButton,
  subStepsWithValidations,
} from '@/store/Engine/constants';

import { Setpoint } from '@/store/Engine/common';
import { PressureLevels } from '@/store/Engine/MainRangesConfiguration';
import CompressorParametersComponent from '../../CompressorParameters/CompressorParameters';
import StationParametersComponent from '../../StationParameters/StationParameters';
import { EngineStepsContainer } from '../styled';
import { EPButton } from '@/components';
import { versionTabs } from '../../CentrifugalCompressors/CentrifugalCompressors.utils';
import { EngineStore } from '@/store/Engine/EngineStore';
import StationStore from '@/store/Station/StationStore';
import DevicesStore from '@/store/Devices/DevicesStore';
import { message } from 'antd';
import { ValidationTriggerFromStepEnum } from '@/store/Engine/constants';

export const generateSetpoints = engineStore => {
  const ignoredCompressorIds = !engineStore.isReadOnly && engineStore.rootStore.devicesStore?.devicesMetadata
    ? new Set(
        engineStore.rootStore.devicesStore.devicesMetadata
          .filter(compressor => {
            const priority = compressor?.attributes?.priority;
            return priority === CompressorPriority.Ignored;
          })
          .map(c => c.id)
      )
    : new Set();

  engineStore.mainRangesConfiguration.pressureLevels?.ranges?.forEach(pressureRange => {
    if (engineStore.setpointsConfiguration?.getSetpointsByPressureRange(pressureRange?.key)?.length) return;
    const orphanSetpoints = engineStore.removeOrphanSetpoints(engineStore.rootStore.devicesStore?.compressors?.length);
    if (orphanSetpoints.length)
      orphanSetpoints.forEach((setpoint: Setpoint, index) =>
        engineStore.setpointsConfiguration?.setpoints?.push(
          new Setpoint({
            ...setpoint,
            parentRangeKey: pressureRange?.key,
            name: `Table${index + 1}`,
            key: uuidv4(),
            oldValues: setpoint,
          }),
        ),
      );
    else engineStore.defaultSetpointsConfiguration?.getSetpointsByPressureRange()?.forEach((setpoint, index) => {
      if (!ignoredCompressorIds.has(setpoint.compressorId)) {
        engineStore.setpointsConfiguration?.setpoints?.push(
          new Setpoint({
            ...setpoint,
            parentRangeKey: pressureRange?.key,
            name: `Table${index + 1}`,
            key: uuidv4(),
            oldValues: setpoint,
          }),
        );
      }
    });
  });
};

export const mapRuleNames = engineStore => {
  engineStore.setpointsConfiguration.setpoints = flattenDeep(
    engineStore.mainRangesConfiguration.flowLevels.ranges.map((flowLevel, flowIndex) =>
      engineStore.mainRangesConfiguration.pressureLevels
        .getPressureRangesByFlow(flowLevel?.key)
        .map((pressureLevel, pressureIndex) =>
          engineStore.setpointsConfiguration?.getSetpointsByPressureRange(pressureLevel?.key).map(setpoint => ({
            ...setpoint,
            name: `Table${
              (engineStore.mainRangesConfiguration.pressureLevels?.ranges?.length /
                engineStore.mainRangesConfiguration.flowLevels.ranges?.length) *
                flowIndex +
              pressureIndex +
              1
            }`,
          })),
        ),
    ),
  );
};

const hasExistingDeletedRule = (defaultBehaviourObject, pressureLevels) =>
  defaultBehaviourObject?.defaultBehaviour === DefaultBehaviour.ExistingRule &&
  !pressureLevels?.ranges?.some(pressureRange => pressureRange?.key === defaultBehaviourObject?.ruleParentKey);

export const hasDeletedRules = engineStore => {
  const hasFlowSensorsPriorityDeletedRules = hasExistingDeletedRule(
    engineStore.sensorsConfiguration?.flowSensorsPriority,
    engineStore.mainRangesConfiguration?.pressureLevels,
  );

  const hasPressureSensorsPriorityDeletedRules = hasExistingDeletedRule(
    engineStore.sensorsConfiguration?.pressureSensorsPriority,
    engineStore.mainRangesConfiguration?.pressureLevels,
  );

  const hasCompressorFallbackDeletedRules = engineStore.fallbackScenarios?.scenarios?.some(scenario =>
    scenario?.fallbackRules?.some(
      fallbackRule =>
        hasExistingDeletedRule(fallbackRule, engineStore.mainRangesConfiguration?.pressureLevels) ||
        (scenario.fallbackBehaviour === FallbackBehaviour.LogicPerFlowLevel &&
          !engineStore.mainRangesConfiguration.flowLevels.ranges.some(
            range => range?.key === fallbackRule.flowRangeKey,
          )),
    ),
  );

  return (
    hasFlowSensorsPriorityDeletedRules || hasPressureSensorsPriorityDeletedRules || hasCompressorFallbackDeletedRules
  );
};

const migrateDeletedRuleToCustomRule = (key, engineStore) => {
  if (
    engineStore.sensorsConfiguration?.[key]?.defaultBehaviour === DefaultBehaviour.ExistingRule &&
    !engineStore.mainRangesConfiguration?.pressureLevels?.ranges?.some(
      pressureRange => pressureRange?.key === engineStore.sensorsConfiguration?.[key]?.ruleParentKey,
    )
  ) {
    engineStore.sensorsConfiguration[key].defaultBehaviour = DefaultBehaviour.CustomRule;
    engineStore.sensorsConfiguration[key].rules = engineStore.setpointsConfiguration
      .getSetpointsByPressureRange(engineStore.sensorsConfiguration?.[key]?.ruleParentKey)
      .map(setpoint => ({ ...setpoint, name: `Table${engineStore.fallbackTableNameCounter + 1}` }));
    engineStore.sensorsConfiguration[key].ruleParentKey = undefined;
  }
};

export const migrateDeletedRulesToCustomRules = engineStore => {
  migrateDeletedRuleToCustomRule('flowSensorsPriority', engineStore);
  migrateDeletedRuleToCustomRule('pressureSensorsPriority', engineStore);
  engineStore.fallbackScenarios?.scenarios?.forEach(scenario =>
    scenario?.fallbackRules?.forEach(fallbackRule => {
      if (
        fallbackRule?.defaultBehaviour === DefaultBehaviour.ExistingRule &&
        !engineStore.mainRangesConfiguration?.pressureLevels?.ranges?.some(
          pressureRange => fallbackRule?.ruleParentKey === pressureRange?.key,
        )
      ) {
        fallbackRule.defaultBehaviour = DefaultBehaviour.CustomRule;
        fallbackRule.rules = engineStore.setpointsConfiguration
          .getSetpointsByPressureRange(fallbackRule?.ruleParentKey)
          .map(setpoint => ({ ...setpoint, name: `Table${engineStore.fallbackTableNameCounter + 1}` }));
        fallbackRule.ruleParentKey = undefined;
      }
    }),
  );
};

interface EngineSubStepInterface {
  name: EngineSubstepsEnum;
  component: (selectedSubstepName?, hasTriedToSubmit?) => JSX.Element;
  selected: boolean;
  completed?: boolean;
  nextDisabled: (engineStore) => boolean;
  storePath?: string;
}

export const allEngineSubsteps = {
  [EngineStepsEnum.CompressorGroups]: [
    {
      name: EngineSubstepsEnum.CompressorsGroups,
      component: (_, hasTriedToSubmit) => <CompressorsGroup hasTriedToSubmit={hasTriedToSubmit} />,
      selected: true,
      nextDisabled: engineStore => engineStore.hasFinishedWizard && !engineStore.compressorGroups?.hasChanges(),
      storePath: 'compressors',
    },
  ],
  [EngineStepsEnum.SensorsConfiguration]: [
    {
      name: EngineSubstepsEnum.FlowSensorsPriority,
      component: (_, hasTriedToSubmit) => <SensorsPriority type="flow" hasTriedToSubmit={hasTriedToSubmit} />,
      selected: true,
      completed: false,
      nextDisabled: engineStore =>
        engineStore.hasFinishedWizard && !engineStore.sensorsConfiguration?.flowSensorsPriority?.hasChanges(),
    },
    {
      name: EngineSubstepsEnum.PressureSensorsPriority,
      component: (_, hasTriedToSubmit) => <SensorsPriority type="pressure" hasTriedToSubmit={hasTriedToSubmit} />,
      selected: false,
      completed: false,
      nextDisabled: engineStore =>
        engineStore.hasFinishedWizard && !engineStore.sensorsConfiguration?.pressureSensorsPriority?.hasChanges(),
    },
    {
      name: EngineSubstepsEnum.SensorsPriorityResult,
      component: () => <SensorsPriorityResult />,
      selected: false,
      completed: false,
      nextDisabled: engineStore => engineStore.hasFinishedWizard && !engineStore.sensorsConfiguration?.hasChanges(),
    },
  ],
  [EngineStepsEnum.MainRangesConfiguration]: [
    {
      name: EngineSubstepsEnum.FlowLevels,
      component: (_, hasTriedToSubmit) => <FlowLevels hasTriedToSubmit={hasTriedToSubmit} />,
      selected: true,
      completed: false,
      nextDisabled: engineStore => !engineStore.mainRangesConfiguration?.flowLevels?.ranges?.length,
    },
    {
      name: EngineSubstepsEnum.PressureLevels,
      component: (_, hasTriedToSubmit) => <PressureLevelsComponent hasTriedToSubmit={hasTriedToSubmit} />,
      selected: false,
      completed: false,
      nextDisabled: engineStore => !engineStore.mainRangesConfiguration?.pressureLevels?.ranges?.length,
    },
    {
      name: EngineSubstepsEnum.MainRangesResult,
      component: selectedSubstepName => <MainRangesResult selectedSubstepName={selectedSubstepName} />,
      selected: false,
      completed: false,
      nextDisabled: engineStore => engineStore.hasFinishedWizard && !engineStore.mainRangesConfiguration?.hasChanges(),
    },
  ],
  [EngineStepsEnum.DefaultSetpointsConfiguration]: [
    {
      name: EngineSubstepsEnum.DefaultSetpoints,
      component: (_, hasTriedToSubmit) => <DefaultSetpoints isEditMode hasTriedToSubmit={hasTriedToSubmit} />,
      selected: true,
      nextDisabled: engineStore =>
        engineStore.hasFinishedWizard && !engineStore.defaultSetpointsConfiguration?.hasChanges(),
      storePath: 'defaultRule',
    },
  ],
  [EngineStepsEnum.StationConfiguration]: [
    {
      name: EngineSubstepsEnum.StationConfiguration,
      component: (_, hasTriedToSubmit) => <StationParametersComponent isEditMode hasTriedToSubmit={hasTriedToSubmit} />,
      selected: true,
      completed: false,
      nextDisabled: engineStore => engineStore.hasFinishedWizard && !engineStore.stationConfiguration?.hasChanges(),
    },
  ],
  [EngineStepsEnum.CompressorParametersConfiguration]: [
    {
      name: EngineSubstepsEnum.CompressorParametersConfiguration,
      component: (_, hasTriedToSubmit) => (
        <CompressorParametersComponent isEditMode hasTriedToSubmit={hasTriedToSubmit} />
      ),
      selected: true,
      completed: false,
      nextDisabled: engineStore => engineStore.hasFinishedWizard && !engineStore.compressorsConfiguration?.hasChanges(),
    },
  ],
};

const EngineStep = observer(
  ({
    step,
    setSelectedStep,
    setActiveTab,
    hasTriedToSubmit,
    setHasTriedToSubmit,
  }: {
    step: EngineStepsEnum;
    setSelectedStep;
    setActiveTab;
    hasTriedToSubmit;
    setHasTriedToSubmit;
  }) => {
    const { t } = useTranslation();
    const { tvalue } = useMeasurement();
    const { engineStore, devicesStore }: { engineStore?: EngineStore; stationStore?: StationStore; devicesStore?: DevicesStore } = useStores();
    const [engineSubsteps, setEngineSubsteps] = useState(allEngineSubsteps[step]);
    const selectedSubstep = (engineSubsteps as EngineSubStepInterface[])?.find(
      engineSubstep => engineSubstep?.selected,
    ) as EngineSubStepInterface;
    const selectedSubstepIndex = indexOf(engineSubsteps as EngineSubStepInterface[], selectedSubstep);
    const isStepCompleted = every(engineSubsteps, { completed: true });
    const [pressureLevelsParentKey] = useState(engineStore.mainRangesConfiguration?.flowLevels?.ranges?.[0]?.key);

    const configurationStatus = engineStore.configurationStatus;
    useEffect(() => {
      if (isStepCompleted) setSelectedStep(selectedStep => selectedStep + 1);
    }, [engineSubsteps]);

    const isValid = () => {
      engineStore.clearValidationMessages();
      if (subStepsWithValidations.includes(selectedSubstep?.name) && ![EngineConfigStatus.Published, EngineConfigStatus.Recommendation].includes(configurationStatus)) {
        engineStore.validate(
          step,
          selectedSubstep?.name,
          selectedSubstep?.name === EngineSubstepsEnum.PressureLevels && pressureLevelsParentKey, // need to know which flow range is selected when validating pressure levels
          selectedSubstep?.name === EngineSubstepsEnum.FlowLevels ||
            selectedSubstep?.name === EngineSubstepsEnum.PressureLevels
            ? undefined // For FlowLevels and PressureLevels, pass undefined as focusedField to show all validation messages
            : devicesStore.devicesMetadata, // For other steps, pass devices as before
            ValidationTriggerFromStepEnum.EngineStep
        );
        setHasTriedToSubmit(true);
        if(engineStore.validationMessages?.length){
          message.error(t(engineStore.validationMessages?.[0]?.message));
        }
        return !engineStore.validationMessages?.length;
      }
      return true;
    };

    const initPressureLevels = () => {
      if (selectedSubstep?.name === EngineSubstepsEnum.FlowLevels) {
        // need to initialise an empty pressure level for the first flow level
        if (!engineStore.hasFinishedWizard)
          engineStore.mainRangesConfiguration.pressureLevels = new PressureLevels(
            engineStore.mainRangesConfiguration.flowLevels,
            engineStore.mainRangesConfiguration.pressureLevels,
          );
      }
      if (selectedSubstep?.name === EngineSubstepsEnum.PressureLevels) {
        engineStore.mainRangesConfiguration?.pressureLevels?.copyPressureRanges(
          engineStore.mainRangesConfiguration.flowLevels?.ranges,
        );
      }
    };

    const initDefinedSetpoints = () => {
      if (
        selectedSubstep?.name === EngineSubstepsEnum.DefaultSetpoints ||
        (engineStore.hasFinishedWizard && selectedSubstep?.name === EngineSubstepsEnum.MainRangesResult)
      )
        generateSetpoints(engineStore);
    };

    const incrementStep = () => {
      if (hasTriedToSubmit) setHasTriedToSubmit(false);
      setEngineSubsteps(engineSubsteps =>
        engineSubsteps.map((substep, index) => ({
          ...substep,
          selected: isStepCompleted ? BASE_PAGE_INDEX : index === selectedSubstepIndex + 1,
          completed: index === selectedSubstepIndex || substep.completed,
        })),
      );
    };

    const onNextClick = async () => {
      if (!isValid()) return;

      // need to initialise an empty pressure range for the first flow level
      initPressureLevels();

      // need to create different setpoints for each pressure range for Rules Editor
      initDefinedSetpoints();

      await engineStore.saveStep(step, tvalue);
      if (!engineStore.error) {
        if (
          selectedSubstep?.name === EngineSubstepsEnum.DefaultSetpoints ||
          (engineStore.hasFinishedWizard && selectedSubstep?.name === EngineSubstepsEnum.MainRangesResult)
        ) {
          mapRuleNames(engineStore);
          await engineStore.saveStep(SetpointsConfiguration, tvalue);
        }

        if (!engineStore.hasFinishedWizard) {
          if (selectedSubstep?.name === EngineSubstepsEnum.DefaultSetpoints) {
            await engineStore.finishWizard(tvalue);
            setActiveTab(versionTabs.draft);
          } else {
            // no need to increment for the Default Setpoints step since it's the last step
            incrementStep();
          }
        }
      }
    };

    const onBackClick = () => {
      if (selectedSubstepIndex === BASE_PAGE_INDEX) setSelectedStep(selectedStep => selectedStep - 1);
      else
        setEngineSubsteps(engineSubsteps =>
          engineSubsteps.map((substep, index) => ({
            ...substep,
            selected: index === selectedSubstepIndex - 1,
          })),
        );
    };

    const onNextRulesGeneratorClick = () => {
      if (!isValid()) return;
      initPressureLevels();
      incrementStep();
    };

    const renderSubsteps = () =>
      engineSubsteps?.map((substep, index) => (
        <div className={cn('substep', !substep.selected && 'low-opacity')} key={substep.name}>
          <span className={cn('substep-circle', index === 2 && 'green-background')}>
            {index === 2 ? <span className={'substep-circle-result'} /> : substep.completed ? <IconCheck /> : null}
          </span>
          <span className="substep-name">{t(substep.name)}</span>
        </div>
      ));

    return (
      <EngineStepsContainer>
        {!engineStore.hasFinishedWizard && <h1 className="engine-step-title">{t(step)}</h1>}
        <div className="flex-row">
          {engineSubsteps?.length > 1 && <div className="substeps">{renderSubsteps()}</div>}
          <div className="selected-substep">
            {engineStore.isLoadingStep ? (
              <SpinCentered />
            ) : (
              selectedSubstep?.component(selectedSubstep?.name, hasTriedToSubmit)
            )}
          </div>
        </div>
        <div className="footer-buttons">
          {(engineStore.hasFinishedWizard
            ? !subStepsWithoutBackButton.includes(selectedSubstep?.name)
            : selectedSubstep?.name !== EngineSubstepsEnum.FlowSensorsPriority) && (
            <EPButton size="medium" type="primary" onClick={onBackClick} className="back-button">
              <span className="button-label">{t('back')}</span>
            </EPButton>
          )}
          {engineStore?.hasFinishedWizard &&
            ((step === EngineStepsEnum.MainRangesConfiguration &&
              selectedSubstep?.name !== EngineSubstepsEnum.MainRangesResult) ||
              (step === EngineStepsEnum.SensorsConfiguration &&
                selectedSubstep?.name !== EngineSubstepsEnum.SensorsPriorityResult)) && (
              <EPButton size="medium" type="primary" onClick={onNextRulesGeneratorClick}>
                <span className="button-label">{t('next')}</span>
              </EPButton>
            )}
          {!engineStore.hasFinishedWizard && (
            <EPButton
              type="primary"
              size="medium"
              onClick={onNextClick}
              disabled={selectedSubstep?.nextDisabled && selectedSubstep?.nextDisabled(engineStore)}
            >
              <span className="button-label">
                {t(
                  !engineStore?.hasFinishedWizard && selectedSubstep?.name === EngineSubstepsEnum.DefaultSetpoints
                    ? 'finish'
                    : engineStore.hasFinishedWizard
                    ? 'save'
                    : 'next',
                )}
              </span>
            </EPButton>
          )}
        </div>
      </EngineStepsContainer>
    );
  },
);

export default EngineStep;
